/**
 * DqIdentityClient - Identity management client for first-party and third-party identity
 *
 * Handles:
 * - First-party identity (1st-Party) - UUID from profile API
 * - Third-party identity (3rd-Party) - UUID from handshake API (Chrome + Google Inc only)
 * - Smart ID selection - prefers 3rd-Party when available, falls back to 1st-Party
 * - localStorage caching with cookie-based sync validation
 */

import { Logger } from "./logger";
import type { IdentityClientConfig } from "./models";
import { mergeConfig } from "./utils";
import { getDqFetch } from "./globalFetch";

export interface IdentityClient {
  initialize(
    config: IdentityClientConfig,
    onInitCallback?: (id?: string) => void,
  ): void;
  getId(): Promise<string>;
  get1pId(): Promise<string>;
  get3pId(): Promise<string | null>;
  isThirdPartyClient(): boolean;
  clearCache(): void;
}

type FirstPartyProfile = {
  i?: string;
};

/**
 * DqIdentityClient implementation
 */
export class DqIdentityClient implements IdentityClient {
  private readonly localStorageKeys = {
    FIRST_PARTY_ID: "dqTag:1pId",
    THIRD_PARTY_ID: "dqTag:3pId",
  } as const;

  private _config!: IdentityClientConfig;
  private _initialized: boolean = false;
  private _logger: Logger;
  private _fetcher: ReturnType<typeof getDqFetch>;
  private readonly _clientSourceValue: string = "dqidentity-sdk";
  private _clientSource: string;
  private _getIdPromise: Promise<string> | null = null;

  constructor() {
    this._logger = new Logger("IdentityClient");
    this._fetcher = getDqFetch(this._logger);
    this._clientSource = this._clientSourceValue;
  }

  /**
   * Initialize the identity client with configuration
   * @param config - Configuration options
   * @throws Error if validation fails
   */
  public initialize(config: IdentityClientConfig = {}): void {
    this._logger.printDebug(
      "Initializing DqIdentityClient with config",
      config,
    );
    this._clientSource = config.clientSource || this._clientSourceValue;
    const fpVersion = config.fpVersion ?? "v4";
    this._config = mergeConfig<IdentityClientConfig>(
      {
        enableThirdParty: true,
        syncAfter: 1800,
        fpVersion,
        firstPartyURL: this._getFirstPartyURL(fpVersion),
        handshakeURL: "https://api.oneid.live/api/v4/handshake",
      },
      config,
    );

    this._validateURL(this._config.firstPartyURL!, "firstParty");
    this._validateURL(this._config.handshakeURL!, "handshake");

    this._initialized = true;
    this._logger.printDebug("DqIdentityClient initialized", this._config);
  }

  /**
   * Check if the identity client is initialized
   * @throws Error if not initialized
   */
  private _ensureInitialized(): void {
    if (!this._initialized) {
      throw new Error(
        "DqIdentityClient not initialized. Call initialize() first.",
      );
    }
  }

  /**
   * Validate first-party URL
   * @param url - URL to validate
   * @throws Error if URL is invalid
   */
  private _validateURL(url: string, urlType: "firstParty" | "handshake"): void {
    try {
      new URL(url);
    } catch {
      throw new Error(`Invalid ${urlType}URL: ${url}. Must be a valid URL.`);
    }
  }

  /**
   * Check if the browser supports third-party identity (Chrome + Google Inc vendor)
   * @returns true if browser is Chrome with Google Inc vendor
   */
  public isThirdPartyClient(): boolean {
    return (
      /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor)
    );
  }

  /**
   * Get identity - smart selector that prefers 3rd-Party when available, falls back to 1st-Party
   * @returns UUID string
   */
  public async getId(): Promise<string> {
    this._ensureInitialized();

    // Deduplicate concurrent getId calls at the method level
    if (this._getIdPromise) {
      this._logger.printDebug("Reusing pending getId() call");
      return this._getIdPromise;
    }

    this._getIdPromise = (async () => {
      try {
        if (this._config.enableThirdParty && this.isThirdPartyClient()) {
          this._logger.printDebug(
            "Browser supports 3rd-Party, attempting handshake",
          );
          const thirdPartyId = await this.get3pId();
          if (thirdPartyId) {
            this._logger.printDebug("Using third-party ID:", thirdPartyId);
            return thirdPartyId;
          }
          this._logger.printDebug(
            "3rd-Party handshake failed, falling back to 1st-Party",
          );
        }

        return await this.get1pId();
      } finally {
        // Clear promise after completion so future calls make fresh requests
        this._getIdPromise = null;
      }
    })();

    return this._getIdPromise;
  }

  /**
   * Get first-party identity UUID
   * @returns UUID string from profile API
   */
  public async get1pId(): Promise<string> {
    this._ensureInitialized();

    const cached = this._getCachedFirstPartyId();
    if (cached && this._isSyncCookieValid()) {
      this._logger.printDebug("Returning cached 1st-Party ID:", cached);
      return cached;
    }

    this._logger.printDebug("Fetching 1st-Party ID from backend");
    const url = this._getFirstPartyURL();

    try {
      const response = await this._fetcher.fetch(
        url,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "X-Client-Source": this._clientSource,
          },
        },
        {
          maxRetries: 3,
          deduplicate: true,
        },
      );

      if (!response.ok) {
        throw new Error(`First-party endpoint returned ${response.status}`);
      }

      const profile = (await response.json()) as FirstPartyProfile;
      const uuid = profile.i;

      if (!uuid) {
        throw new Error("No ID found in profile response");
      }

      localStorage.setItem(this.localStorageKeys.FIRST_PARTY_ID, uuid);

      document.cookie = `DqSync=true; max-age=${this._config.syncAfter}; path=/`;

      this._logger.printDebug("Cached 1st-Party ID:", uuid);

      return uuid;
    } catch (error) {
      this._logger.printError("Failed to fetch 1st-Party ID:", error);
      throw error;
    }
  }

  /**
   * Get third-party identity UUID via handshake
   * @returns UUID string from handshake API, or null if not available
   */
  public async get3pId(): Promise<string | null> {
    this._ensureInitialized();

    if (!this._config.enableThirdParty || !this.isThirdPartyClient()) {
      this._logger.printDebug("3rd-party identity not supported or disabled");
      return null;
    }

    const cached = this._getCachedThirdPartyId();
    if (cached && this._isSyncCookieValid()) {
      this._logger.printDebug("Returning cached 3rd-Party ID:", cached);
      return cached;
    }

    this._logger.printDebug("Calling 3rd-Party handshake endpoint");
    const url = this._config.handshakeURL!;

    // Note: Handshake endpoint returns plain text, not JSON, so we use native fetch
    // with manual retry logic instead of DqFetch (which assumes JSON responses)
    return this._fetchHandshakeWithRetry(url, 3);
  }

  /**
   * Fetch handshake with retry logic
   * Note: Handshake returns plain text, not JSON
   */
  private async _fetchHandshakeWithRetry(
    url: string,
    maxRetries: number,
  ): Promise<string | null> {
    try {
      this._logger.printDebug("Getting 3P ID from handshake (GET)");

      // First attempt: GET request
      const getResponse = await this._fetcher.fetch(
        url,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "X-Client-Source": this._clientSource,
          },
        },
        {
          maxRetries,
          deduplicate: true,
        },
      );

      if (!getResponse.ok) {
        this._logger.printError(`Handshake GET returned ${getResponse.status}`);
        return null;
      }

      let thirdPartyId = await getResponse.text();

      if (thirdPartyId && thirdPartyId.trim() !== "") {
        this._cacheThirdPartyId(thirdPartyId.trim());
        return thirdPartyId.trim();
      }

      // GET returned empty, try POST with 1P ID
      const firstPartyId = this._getCachedFirstPartyId();

      if (!firstPartyId) {
        this._logger.printDebug("No 1P ID available for POST");
        return null;
      }

      this._logger.printDebug(
        "GET returned empty, posting 1P ID to create 3P ID:",
        firstPartyId,
      );

      const postResponse = await this._fetcher.fetch(
        url,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "X-Client-Source": this._clientSource,
          },
          body: JSON.stringify({
            r: window.location.origin,
            e: {},
            k: [],
            c: [],
          }),
        },
        {
          maxRetries,
        },
      );

      if (!postResponse.ok) {
        this._logger.printError(
          `Handshake POST returned ${postResponse.status}`,
        );
        return null;
      }

      thirdPartyId = await postResponse.text();

      if (!thirdPartyId || thirdPartyId.trim() === "") {
        this._logger.printDebug("Handshake POST returned empty ID");
        return null;
      }

      this._cacheThirdPartyId(thirdPartyId.trim());
      return thirdPartyId.trim();
    } catch (error) {
      this._logger.printError("Failed to fetch 3rd-Party ID:", error);
      return null;
    }
  }

  /**
   * Helper to cache third-party ID
   */
  private _cacheThirdPartyId(thirdPartyId: string): void {
    localStorage.setItem(this.localStorageKeys.THIRD_PARTY_ID, thirdPartyId);

    document.cookie = `DqSync=true; max-age=${this._config.syncAfter}; path=/`;

    this._logger.printDebug("Cached 3rd-Party ID:", thirdPartyId);
  }

  /**
   * Clear cached identity data from localStorage and cookies
   */
  public clearCache(): void {
    // Remove localStorage items
    localStorage.removeItem(this.localStorageKeys.FIRST_PARTY_ID);
    localStorage.removeItem(this.localStorageKeys.THIRD_PARTY_ID);

    // Clear sync cookie
    document.cookie = "DqSync=; max-age=0; path=/";

    this._logger.printDebug("Cleared identity cache");
  }

  /**
   * Get cached first-party ID from localStorage
   * @returns Cached UUID or null
   */
  private _getCachedFirstPartyId(): string | null {
    return localStorage.getItem(this.localStorageKeys.FIRST_PARTY_ID);
  }

  /**
   * Get cached third-party ID from localStorage
   * @returns Cached UUID or null
   */
  private _getCachedThirdPartyId(): string | null {
    return localStorage.getItem(this.localStorageKeys.THIRD_PARTY_ID);
  }

  /**
   * Check if the OneIdSDKSync cookie is valid (indicates cache freshness)
   * @returns true if sync cookie exists
   */
  private _isSyncCookieValid(): boolean {
    return document.cookie.indexOf("DqSync") !== -1;
  }

  private _getFirstPartyURL(
    fpVersion?: IdentityClientConfig["fpVersion"],
  ): string {
    if (this._config?.firstPartyURL) {
      return this._config.firstPartyURL;
    }
    const domainParts = window.location.hostname.split(".").reverse();
    const tld = domainParts[0];
    const domain = domainParts[1];
    const version = fpVersion ?? this._config.fpVersion;

    // Constructs: https://oneid.[domain].[tld]/api/[version]/firstparty
    return `https://oneid.${domain}.${tld}/api/${version}/firstparty`;
  }
}
